// ==== URL d'API dynamique selon environnement ====

let API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;
if (!API_BASE_URL) {
	if (typeof window !== 'undefined') {
		API_BASE_URL =
			window.location.hostname === 'localhost'
				? 'http://localhost:4000/api'
				: 'https://api.les-doudous.com/api';
	} else {
		API_BASE_URL = 'https://api.les-doudous.com/api';
	}
}

export interface AttemptInfo {
	totalAttempts: number;
	remainingAttempts: number;
	canPlay: boolean;
	lastAttemptDate?: string;
}

export interface BestScore {
	game_type: string;
	team: 'julia' | 'benjamin';
	best_score: number;
	max_score: number;
	best_percentage: number;
	total_attempts: number;
}

export interface ApiResponse<T> {
	success: boolean;
	message?: string;
	data?: T;
	errors?: string[];
	pagination?: {
		current_page: number;
		total_pages: number;
		per_page: number;
		total: number;
	};
}

export interface AdminLoginData {
	password: string;
}

export interface AdminStats {
	total_guests: number;
	confirmed_guests: number;
	pending_guests: number;
	declined_guests: number;
	total_messages: number;
	approved_messages: number;
	total_donations: number;
	total_amount: number;
	total_game_results: number;
}

export interface GuestGroup {
	id: number;
	group_name: string;
	group_type: 'solo' | 'couple' | 'family';
	members?: Guest[];
	total_members?: number;
	confirmed_members?: number;
}

export interface Guest {
	id: number;
	group_id?: number;
	first_name: string;
	last_name: string;
	rsvp_status: 'confirmed' | 'declined' | 'pending';
	number_of_guests: number;
	attending_pre_event?: 'yes' | 'no' | 'maybe';
	dietary_restrictions?: string;
	plus_one_name?: string;
	message?: string;
	is_main_contact: boolean;
	group_name?: string;
	group_type?: string;
	created_at: string;
	updated_at: string;
}

export interface AutocompleteResult {
	id: number;
	firstName: string;
	lastName: string;
	group_id?: number;
	is_main_contact: boolean;
	group_name?: string;
	group_type?: string;
	is_child?: boolean | number;
	attending_pre_event?: string;
}

// Interface pour la soumission RSVP de groupe
export interface GroupRSVPData {
	decision_maker_id: number;
	group_id: number;
	members: Array<{
		id: number;
		rsvp_status: 'confirmed' | 'declined';
		attending_pre_event?: 'yes' | 'no' | null;
		message?: string | null;
	}>;
}

export interface Message {
	id: number;
	author_name: string;
	message: string;
	is_approved: boolean;
	created_at: string;
}

export interface GameResult {
	id: number;
	playerName: string;
	game_type: string;
	score: number;
	totalQuestions?: number;
	team: 'julia' | 'benjamin';
	details?: any;
	attempt: number;
	createdAt: string;
}

// ============================================
// INTERFACES POUR LES MÉDIAS
// ============================================

export interface MediaItem {
	id: number;
	uploader_name: string;
	message: string | null;
	type: 'image' | 'video';
	file_url: string;
	thumbnail_url: string | null;
	is_approved: boolean;
	file_size: number;
	formatted_size: string;
	mime_type: string;
	original_name: string;
	created_at: string;
}

export interface MediaStats {
	total_media: number;
	total_images: number;
	total_videos: number;
	approved_media: number;
	pending_media: number;
	total_size: number;
}

export interface MediaUploadData {
	file: File;
	uploader_name: string;
	message?: string;
	type: 'image' | 'video';
}

class ApiService {
	private getAuthHeaders(): HeadersInit {
		if (typeof window === 'undefined') return {};
		const token = localStorage.getItem('admin-token');
		return token ? { 'Authorization': `Bearer ${token}` } : {};
	}

	private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
		try {
			const headers = {
				'Content-Type': 'application/json',
				...this.getAuthHeaders(),
				...options.headers,
			};
			const response = await fetch(`${API_BASE_URL}${endpoint}`, {
				headers,
				...options,
			});
			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.message || 'Erreur API');
			}
			return data;
		} catch (error) {
			console.error('Erreur API:', error);
			throw error;
		}
	}

	// NOUVELLE méthode pour RSVP de groupe individuel
	async updateGroupRSVP(data: GroupRSVPData): Promise<ApiResponse<any>> {
		return this.request('/rsvp', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	// Test de connexion
	async ping(): Promise<ApiResponse<{ message: string }>> {
		return this.request('/ping');
	}

	// === ADMIN ===
	async adminLogin(loginData: AdminLoginData): Promise<ApiResponse<{ token: string }>> {
		const response = await this.request<{ token: string }>('/admin/login', {
			method: 'POST',
			body: JSON.stringify(loginData),
		});
		if (response.success && response.data?.token) {
			localStorage.setItem('admin-token', response.data.token);
		}
		return response;
	}

	async adminLogout(): Promise<void> {
		localStorage.removeItem('admin-token');
	}

	async verifyAdminToken(): Promise<boolean> {
		try {
			const response = await this.request('/admin/verify');
			return response.success;
		} catch {
			localStorage.removeItem('admin-token');
			return false;
		}
	}

	// === ADMIN DATA ===
	async getAdminStats(): Promise<ApiResponse<AdminStats>> {
		return this.request('/admin/stats');
	}

	async getAllGuests(): Promise<ApiResponse<Guest[]>> {
		const res = await this.request<Guest[]>('/admin/guests');
		if (res.success && Array.isArray(res.data)) {
			res.data = res.data.map((guest: Guest) => ({
				...guest,
				attending_pre_event:
					guest.attending_pre_event === 'yes' ? 'yes'
						: guest.attending_pre_event === 'no' ? 'no'
							: 'no'
			}));
		}
		return res;
	}

	async createGuest(guestData: Omit<Guest, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Guest>> {
		return this.request('/admin/guests', {
			method: 'POST',
			body: JSON.stringify(guestData),
		});
	}

	async updateGuest(id: number, guestData: Partial<Omit<Guest, 'id' | 'created_at' | 'updated_at'>>): Promise<ApiResponse<Guest>> {
		return this.request(`/admin/guests/${id}`, {
			method: 'PUT',
			body: JSON.stringify(guestData),
		});
	}

	async deleteGuest(id: number): Promise<ApiResponse<any>> {
		return this.request(`/admin/guests/${id}`, {
			method: 'DELETE',
		});
	}

	async getAllMessages(): Promise<ApiResponse<Message[]>> {
		return this.request('/admin/messages');
	}

	// === GAME RESULTS ===
	async getAllGameResults(): Promise<ApiResponse<GameResult[]>> {
		return this.request('/admin/game-results');
	}

	async deleteGameResult(id: number): Promise<ApiResponse<any>> {
		return this.request(`/admin/game-results/${id}`, {
			method: 'DELETE',
		});
	}

	// Vérifier les tentatives d'un utilisateur
	async checkUserAttempts(userId: string): Promise<ApiResponse<AttemptInfo>> {
		return this.request(`/game-attempts/${userId}`);
	}

	// Soumettre les résultats avec gestion des tentatives
	async submitGameResultsWithAttempts(payload: {
		userId: string;
		playerName: string;
		team: 'julia' | 'benjamin';
		results: Array<{
			game_type: string;
			score: number;
			max_score: number;
		}>;
	}): Promise<ApiResponse<any>> {
		return this.request('/game-attempts/submit', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	// Récupérer les meilleurs scores d'un utilisateur
	async getBestScores(userId: string): Promise<ApiResponse<BestScore[]>> {
		return this.request(`/game-attempts/best-scores/${userId}`);
	}

	async submitGameResults(payload: {
		playerName: string;
		results: Array<{
			game_type: string;
			score: number;
			totalQuestions?: number;
			team: string;
		}>
	}): Promise<ApiResponse<any>> {
		try {
			return await this.request('/game-results', {
				method: 'POST',
				body: JSON.stringify(payload),
			});
		} catch (error: any) {
			if (error.message && (
				error.message.includes('Nombre maximum d\'essais atteint') ||
				error.message.includes('tentatives')
			)) {
				return {
					success: false,
					message: 'Vous avez atteint vos 2 tentatives quotidiennes',
				};
			}
			throw error;
		}
	}

	// === AUTOCOMPLETE & GROUPS ===
	async searchGuests(query: string): Promise<ApiResponse<AutocompleteResult[]>> {
		return this.request(`/autocomplete/search?q=${encodeURIComponent(query)}`);
	}

	async getGuestsByGroupId(groupId: number): Promise<ApiResponse<any[]>> {
		return this.request(`/guests?group_id=${groupId}`);
	}

	async getAllGroups(): Promise<ApiResponse<GuestGroup[]>> {
		return this.request('/autocomplete/groups');
	}

	async createGroup(groupData: Omit<GuestGroup, 'id'>): Promise<ApiResponse<{ id: number }>> {
		return this.request('/autocomplete/groups', {
			method: 'POST',
			body: JSON.stringify(groupData),
		});
	}

	// ============================================
	// MÉTHODES POUR LES MÉDIAS
	// ============================================

	/**
	 * Upload de photos/vidéos après le mariage (PUBLIC)
	 * @param formData FormData contenant le fichier et les métadonnées
	 * @param onProgress Callback optionnel pour suivre la progression
	 */
	async uploadMedia(
		formData: FormData,
		onProgress?: (progress: number) => void
	): Promise<ApiResponse<any>> {
		try {
			const xhr = new XMLHttpRequest();

			return new Promise((resolve, reject) => {
				// Suivi de la progression
				if (onProgress) {
					xhr.upload.addEventListener('progress', (e) => {
						if (e.lengthComputable) {
							const percentComplete = Math.round((e.loaded / e.total) * 100);
							onProgress(percentComplete);
						}
					});
				}

				// Gestion de la réponse
				xhr.addEventListener('load', () => {
					if (xhr.status >= 200 && xhr.status < 300) {
						try {
							const response = JSON.parse(xhr.responseText);
							resolve(response);
						} catch (error) {
							reject(new Error('Erreur de parsing de la réponse'));
						}
					} else {
						try {
							const errorResponse = JSON.parse(xhr.responseText);
							reject(new Error(errorResponse.message || 'Erreur lors de l\'upload'));
						} catch {
							reject(new Error(`Erreur HTTP: ${xhr.status}`));
						}
					}
				});

				// Gestion des erreurs réseau
				xhr.addEventListener('error', () => {
					reject(new Error('Erreur réseau lors de l\'upload'));
				});

				xhr.addEventListener('abort', () => {
					reject(new Error('Upload annulé'));
				});

				// Envoi de la requête
				xhr.open('POST', `${API_BASE_URL}/media/upload`);

				// Ajouter le token d'auth si disponible (pas nécessaire pour l'upload public)
				const token = typeof window !== 'undefined' ? localStorage.getItem('admin-token') : null;
				if (token) {
					xhr.setRequestHeader('Authorization', `Bearer ${token}`);
				}

				xhr.send(formData);
			});
		} catch (error) {
			console.error('Upload error:', error);
			return {
				success: false,
				message: error instanceof Error ? error.message : 'Erreur inconnue lors de l\'upload'
			};
		}
	}

	/**
	 * Récupérer les médias approuvés (PUBLIC)
	 * Pour affichage public sur le site
	 */
	async getApprovedMedia(options?: {
		type?: 'image' | 'video';
		page?: number;
	}): Promise<ApiResponse<MediaItem[]>> {
		const params = new URLSearchParams();
		if (options?.type) params.append('type', options.type);
		if (options?.page) params.append('page', options.page.toString());

		return this.request(`/media/approved?${params.toString()}`);
	}

	// ============================================
	// MÉTHODES ADMIN POUR LES MÉDIAS
	// ============================================

	/**
	 * Récupérer tous les médias uploadés (ADMIN)
	 * Avec filtres et pagination
	 */
	async getAllMedia(options?: {
		is_approved?: number;
		type?: string;
		category?: string;
		page?: number;
	}): Promise<ApiResponse<MediaItem[]>> {
		const params = new URLSearchParams();
		if (options?.is_approved !== undefined) params.append('is_approved', options.is_approved.toString());
		if (options?.type) params.append('type', options.type);
		if (options?.page) params.append('page', options.page.toString());

		return this.request(`/media/admin/all?${params.toString()}`);
	}

	/**
	 * Récupérer les statistiques des médias (ADMIN)
	 */
	async getMediaStats(): Promise<ApiResponse<MediaStats>> {
		return this.request('/media/admin/stats');
	}

	/**
	 * Approuver un média (ADMIN)
	 * Rend le média visible publiquement
	 */
	async approveMedia(id: number): Promise<ApiResponse<any>> {
		return this.request(`/media/admin/${id}/approve`, {
			method: 'PATCH',
		});
	}

	/**
	 * Supprimer un média (ADMIN)
	 * Supprime le fichier et l'entrée en base de données
	 */
	async deleteMedia(id: number): Promise<ApiResponse<any>> {
		return this.request(`/media/admin/${id}`, {
			method: 'DELETE',
		});
	}
}

export const apiService = new ApiService();
